<template>
  <div class="week-filtered-view" v-if="hasDays && hasTimeSlots">
    <div class="week-filtered-header">
      <div class="week-filtered-corner"></div>
      <div
        v-for="(day, index) in days"
        :key="`${day.date}-${index}`"
        class="week-filtered-header-cell"
        :class="{ 'week-filtered-header-cell--today': isToday(day.date) }"
      >
        <div class="week-filtered-header-weekday">{{ day.weekday }}</div>
        <div class="week-filtered-header-date">{{ day.displayDate }}</div>
      </div>
    </div>
    <div class="week-filtered-times" :style="{ height: `${calendarHeight}px` }">
      <div
        v-for="slot in filteredTimeSlots"
        :key="slot.key"
        class="week-filtered-time"
        :class="{ 'week-filtered-time--with-label': slot.label }"
        :style="{ height: `${slotHeight}px` }"
      >
        <span v-if="slot.label">{{ slot.label }}</span>
      </div>
    </div>
    <div class="week-filtered-days">
      <div
        v-for="(day, index) in days"
        :key="`column-${day.date}-${index}`"
        class="week-filtered-day-column"
        :style="{ height: `${calendarHeight}px` }"
      >
        <div class="week-filtered-events">
          <week-appointment-card
            v-for="(appointment, appointmentIndex) in getDayAppointmentsWithPosition(day)"
            :key="appointment.key || appointmentIndex"
            :data="appointment.data"
            :style="appointment.style"
            :compact="true"
            class="week-filtered-card"
            @deleteMultipleAppointments="onDeleteMultipleAppointments"
            @openModalInsuranceList="onOpenModalInsuranceList"
            @openModalExpenses="onOpenModalExpenses"
            @infoWaitingList="onInfoWaitingList"
          />
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="hasDays && !hasTimeSlots" class="empty">
    <div class="empty-icon">
      <fa-icon :icon="['fal', 'calendar-alt']" size="3x"/>
    </div>
    <p class="empty-title h5">Sem agenda</p>
    <p class="empty-subtitle">Não existe configuração de agenda para esta data</p>
  </div>
  <div v-else class="empty">
    <div class="empty-icon">
      <fa-icon :icon="['fal', 'calendar-alt']" size="3x"/>
    </div>
    <p class="empty-title h5">Sem agenda</p>
    <p class="empty-subtitle">Não existe configuração de agenda para esta data</p>
  </div>
</template>

<script>
import moment from 'moment';
import WeekAppointmentCard from '@/app/appointment/components/view/WeekAppointmentCard.vue';

export default {
  name: 'WeekFilteredView',
  components: {
    WeekAppointmentCard,
  },
  props: {
    days: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasDays() {
      return Array.isArray(this.days) && this.days.length > 0;
    },
    hasTimeSlots() {
      return this.filteredTimeSlots.length > 0;
    },
    slotHeight() {
      return 60;
    },
    appointmentTimeSlots() {
      if (!this.hasDays) {
        return [];
      }

      const timeSlotsSet = new Set();

      this.days.forEach((day) => {
        if (day.appointments && Array.isArray(day.appointments)) {
          day.appointments.forEach((appointment) => {
            if (appointment.startDate) {
              const start = moment(appointment.startDate);
              if (start.isValid()) {
                const minuteKey = start.format('HHmm');
                timeSlotsSet.add(minuteKey);
              }
            }
          });
        }
      });

      const sortedSlots = Array.from(timeSlotsSet)
        .map((minuteKey) => {
          const hour = parseInt(minuteKey.substring(0, 2), 10);
          const minute = parseInt(minuteKey.substring(2, 4), 10);
          return {
            key: minuteKey,
            hour,
            minute,
            totalMinutes: hour * 60 + minute,
          };
        })
        .sort((a, b) => a.totalMinutes - b.totalMinutes);

      return sortedSlots;
    },
    filteredTimeSlots() {
      if (this.appointmentTimeSlots.length === 0) {
        return [];
      }

      return this.appointmentTimeSlots.map((slot) => {
        const slotMoment = moment().hour(slot.hour).minute(slot.minute).second(0);
        return {
          key: slot.key,
          label: slotMoment.format('HH:mm'),
        };
      });
    },
    calendarHeight() {
      return this.filteredTimeSlots.length * this.slotHeight;
    },
  },
  methods: {
    isToday(date) {
      return moment(date).isSame(moment(), 'day');
    },
    onDeleteMultipleAppointments(payload) {
      this.$emit('deleteMultipleAppointments', payload);
    },
    onOpenModalInsuranceList(payload) {
      this.$emit('openModalInsuranceList', payload);
    },
    onOpenModalExpenses(payload) {
      this.$emit('openModalExpenses', payload);
    },
    onInfoWaitingList() {
      this.$emit('infoWaitingList');
    },
    getDayAppointmentsWithPosition(day) {
      if (!day || !Array.isArray(day.appointments) || day.appointments.length === 0) {
        return [];
      }

      if (this.filteredTimeSlots.length === 0) {
        return day.appointments.map(appointment => ({
          data: appointment,
          style: {},
        }));
      }

      const timeSlotMap = {};
      this.filteredTimeSlots.forEach((slot, index) => {
        timeSlotMap[slot.key] = index;
      });

      return day.appointments
        .map((appointment) => {
          if (!appointment.startDate) {
            return null;
          }

          const start = moment(appointment.startDate);
          if (!start.isValid()) {
            return null;
          }

          const minuteKey = start.format('HHmm');
          const slotIndex = timeSlotMap[minuteKey];

          if (slotIndex === undefined) {
            return null;
          }

          const top = slotIndex * this.slotHeight;

          return {
            data: appointment,
            style: {
              position: 'absolute',
              top: `${top}px`,
              left: '0',
              right: '0',
              width: '100%',
              height: '60px',
            },
          };
        })
        .filter(item => item !== null);
    },
  },
};
</script>

<style lang="scss">
@import 'src/assets/scss/variables';

.week-filtered-view {
  display: grid;
  grid-template-columns: 50px 1fr;
  grid-template-rows: auto 1fr;
}

.week-filtered-header {
  display: grid;
  grid-column: 1 / 3;
  grid-row: 1 / 2;
  grid-template-columns: 50px repeat(7, minmax(0, 1fr));
  position: sticky;
  top: 2.2rem;
  background-color: $light-color;
  z-index: 101;
  border-top: $border-width solid $border-color;
  border-right: $border-width solid $border-color;
  height: 100%;
  align-items: stretch;
}

.week-filtered-corner {
  background-color: $light-color;
  height: 100%;
  min-height: 4rem;
  border-bottom: $border-width solid $border-color;
}

.week-filtered-header-cell {
  padding: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 0;
  border-radius: 0;
  background-color: $light-color;
  border-bottom: $border-width solid $border-color;
  border-left: $border-width solid $border-color;
  min-height: 4rem;
  padding: 0.5rem $layout-spacing-sm;
  align-items: center;
  height: 100%;
}

.week-filtered-header-cell--today {
  border-bottom-color: $border-color;
  border-left-color: $border-color;
}

.week-filtered-header-weekday {
  font-weight: 400;
  font-size: 0.5rem;
  color: $gray-color;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.week-filtered-header-date {
  color: $gray-color-dark;
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.week-filtered-header-cell--today .week-filtered-header-date {
  background-color: rgba($primary-color, 0.12);
  color: darken($primary-color, 10%);
  font-weight: 500;
}

.week-filtered-times {
  display: flex;
  flex-direction: column;
  grid-column: 1 / 2;
  grid-row: 2 / 3;
  background-color: $light-color;
  border-bottom: $border-width solid $border-color;
  position: relative;
}

.week-filtered-time {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  font-size: $font-size-xs;
  color: $gray-color-dark;
  padding-right: 0.25rem;
  position: relative;
}

.week-filtered-time--with-label span {
  position: absolute;
  top: 0;
  right: 0.25rem;
  line-height: 1;
  transform: translateY(-50%);
}

.week-filtered-times > .week-filtered-time:first-child.week-filtered-time--with-label span {
  top: 0.5rem;
}

.week-filtered-days {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  position: relative;
  width: 100%;
  grid-column: 2 / 3;
  grid-row: 2 / 3;
  border-right: $border-width solid $border-color;
  border-bottom: $border-width solid $border-color;
}

.week-filtered-day-column {
  position: relative;
  background-color: $bg-color-dark;
  border-left: $border-width solid $border-color;
  border-radius: 0;
  overflow: visible;
  min-width: 0;
}

.week-filtered-events {
  position: relative;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
}

.week-filtered-card {
  width: 100%;
  margin-bottom: 0 !important;
  height: 60px;
  min-height: 60px;
  max-height: 60px;
  overflow: hidden;
}


@media screen and (max-width: $size-lg) {
  .week-filtered-view {
    grid-template-columns: 3.5rem 1fr;
  }

  .week-filtered-header {
    grid-template-columns: 3.5rem repeat(7, minmax(0, 1fr));
  }
}
</style>
